import React, { memo, useMemo } from 'react';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { DiaryEntrySyncStatus } from 'graphql/types';
import { SmartDiaryEntry } from '../type';
import { CustomCPTLabel } from './CustomCPTLabel';
import { SmartListItemDuration } from './SmartListItemDuration';

type SmartListItemTextProps = {
  smartEntry: SmartDiaryEntry;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  delete: {
    border: `1px solid ${theme.palette.error.dark}`,
    borderRadius: theme.spacing(0.5),
    ...theme.typography.caption,
    color: theme.palette.error.dark,
    alignSelf: 'center',
    padding: theme.spacing(0, 0.5),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
}));

export const SmartListItemText: React.FC<SmartListItemTextProps> = memo(
  ({ smartEntry }: SmartListItemTextProps) => {
    const classes = useStyles();
    const showMarkToDelete = useIsMarkedToDelete(smartEntry);

    return (
      <div className={classes.root}>
        <CustomCPTLabel smartEntry={smartEntry} />
        {showMarkToDelete && (
          <Typography
            data-testid={`smart-entry-item_markedToDelete_${smartEntry.id}`}
            className={classes.delete}
          >
            <FormattedMessage id="smartDiary.willRemove" />
          </Typography>
        )}
        {<SmartListItemDuration smartEntry={smartEntry} />}
      </div>
    );
  }
);

SmartListItemText.displayName = 'SmartListItemText';

export const useIsMarkedToDelete = (smartEntry: SmartDiaryEntry): boolean => {
  const { control, getValues } = useFormContext();
  const value = useWatch({
    name: smartEntry.id,
    control,
    defaultValue: getValues()[smartEntry.id]
  });

  return useMemo(
    () => smartEntry?.syncStatus === DiaryEntrySyncStatus.Synced && value,
    [smartEntry?.syncStatus, value]
  );
};
